import { useState } from 'react';

import { Modal } from './common';

/**
 * Frequencies to avoid — one tap from the header, on every screen.
 *
 * The list comes first and stays short. The reasoning sits behind a link, so
 * it's there when wanted and out of the way when not.
 */

type Band = {
  range: string;
  short: string;
  why: string[];
};

const BANDS: Band[] = [
  {
    range: '13 – 30 Hz',
    short: 'Highest caution. Avoid entirely for anyone with a seizure history.',
    why: [
      'Four separate lines of evidence bottom out in this range, which is unusual — they come from different fields and were not derived from each other.',
      'Photosensitive seizure provocation peaks around 15–20 Hz. This is the same range used in clinical photic-stimulation testing to deliberately elicit abnormal EEG activity.',
      'The magnetophosphene threshold — the weakest magnetic field that produces visual flickering — reaches its minimum at 20 Hz. That is the field strength at which a magnetic field first does something perceptible to neural tissue.',
      'ICNIRP exposure restrictions are at their most conservative between 10 and 25 Hz.',
      'The IEEE dosimetric reference limit for brain tissue reaches its minimum at 20 Hz.',
      'Sweeps matter as much as fixed frequencies here. A sweep from 8 to 40 Hz never names a blocked value but steps through the entire band on its way.',
    ],
  },
  {
    range: '45 – 65 Hz',
    short: 'High caution. Includes mains frequency.',
    why: [
      'This is the range neurosurgeons use during cortical mapping to deliberately provoke seizures, in order to locate functional tissue before operating.',
      'He 2013 measured neuronal sodium current density increasing 30–125% at 50 Hz and around 1 mT — a field strength within this device’s range.',
      'Varró 2009 found increased neocortical seizure susceptibility at 250–500 µT.',
      'It is also mains frequency in most of the world, which is why ambient 50/60 Hz exposure is studied so heavily and why the animal literature here is unusually large.',
    ],
  },
];

const IMPLANT_WHY = [
  'A pulsed magnetic field can interfere with implanted electronics. This is different in kind from the frequency questions — it is a device-to-device interaction, not a question of dose, so turning the intensity down does not address it.',
  'It appears as a contraindication in the labelling of every FDA-approved PEMF device reviewed, and it is the only warning that appears identically across all of them. Most of the other warnings in that labelling turn out to be inherited boilerplate; this one is not.',
  'It applies to the person the coil is near, not just the person holding the phone.',
  'The app does not screen for this or enforce it — it is here so it is known.',
];

const PLACEMENT_WHY = [
  'At the face of the coil, the field reaches roughly 1.5 times the magnetophosphene threshold — enough to produce visual flickering.',
  'At the abdomen or a limb, the head sees about 3.4 mV/m, which is far below any stimulation threshold.',
  'The entire difference is distance. The field is not intrinsically weak — it falls off steeply, and body placement relies on that falloff. Moving the coil to the head removes the margin rather than reducing it.',
  'This is why head placement is a separate setting rather than just another frequency choice.',
];

export function AvoidButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        className="btn small ghost"
        onClick={() => setOpen(true)}
        aria-label="Frequencies to avoid"
        title="Frequencies to avoid"
        style={{ fontSize: 15, lineHeight: 1, padding: '6px 9px' }}
      >
        ⚠
      </button>
      {open ? <AvoidSheet onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function AvoidSheet({ onClose }: { onClose: () => void }) {
  const [showWhy, setShowWhy] = useState<string | null>(null);

  return (
    <Modal title="Frequencies to avoid" onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        Relevant with a seizure history, photosensitivity, or an implanted device.
      </p>

      <div className="stack">
        {BANDS.map((band) => (
          <div className="issue error" key={band.range}>
            <strong className="mono" style={{ fontSize: 16 }}>
              {band.range}
            </strong>
            <div className="faint" style={{ marginTop: 4 }}>
              {band.short}
            </div>
            <button
              className="btn small ghost"
              style={{ padding: 0, border: 0, marginTop: 6 }}
              onClick={() => setShowWhy(band.range)}
            >
              Why →
            </button>
          </div>
        ))}

        <div className="issue warning">
          <strong>Head, neck & chest placement</strong>
          <div className="faint" style={{ marginTop: 4 }}>
            A placement question rather than a frequency one.
          </div>
          <button
            className="btn small ghost"
            style={{ padding: 0, border: 0, marginTop: 6 }}
            onClick={() => setShowWhy('placement')}
          >
            Why →
          </button>
        </div>

        <div className="issue error">
          <strong>Pacemakers & implanted electronics</strong>
          <div className="faint" style={{ marginTop: 4 }}>
            Do not use the device on anyone with a pacemaker, implanted defibrillator, deep brain
            or vagus nerve stimulator, cochlear implant, or insulin pump.
          </div>
          <button
            className="btn small ghost"
            style={{ padding: 0, border: 0, marginTop: 6 }}
            onClick={() => setShowWhy('implants')}
          >
            Why →
          </button>
        </div>
      </div>

      <hr className="divider" />

      <p className="faint" style={{ marginBottom: 0 }}>
        Generally further from known thresholds: <strong className="mono">0.5–12 Hz</strong>,{' '}
        <strong className="mono">31–44 Hz</strong>, <strong className="mono">66–200 Hz</strong>.
      </p>

      {showWhy ? (
        <WhyModal
          title={
            showWhy === 'placement'
              ? 'Head, neck & chest placement'
              : showWhy === 'implants'
                ? 'Pacemakers & implanted electronics'
                : showWhy
          }
          points={
            showWhy === 'placement'
              ? PLACEMENT_WHY
              : showWhy === 'implants'
                ? IMPLANT_WHY
                : (BANDS.find((b) => b.range === showWhy)?.why ?? [])
          }
          showFrequencyNote={showWhy !== 'implants'}
          onClose={() => setShowWhy(null)}
        />
      ) : null}
    </Modal>
  );
}

function WhyModal({
  title,
  points,
  showFrequencyNote = true,
  onClose,
}: {
  title: string;
  points: string[];
  showFrequencyNote?: boolean;
  onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <ul className="caveat-list" style={{ marginTop: 0 }}>
        {points.map((p, i) => (
          <li key={i} style={{ marginBottom: 9 }}>
            {p}
          </li>
        ))}
      </ul>
      {showFrequencyNote ? (
        <>
          <hr className="divider" />
          <p className="faint" style={{ marginBottom: 0 }}>
            One thing that is often assumed and is not true: “low frequency calms, high frequency
            excites” does not hold. 1 Hz appears as both a therapeutic protocol and a sham control;
            145–200 Hz is anti-seizure in implanted stimulators; 50–60 Hz provokes. Frequency alone
            does not predict the effect.
          </p>
        </>
      ) : null}
    </Modal>
  );
}
